import barba from "@barba/core";
import Lenis from "lenis";

import {gsap} from "gsap";
import MouseFollower from "mouse-follower";
import {ScrollTrigger} from "gsap/ScrollTrigger";
import {Observer} from "gsap/Observer";

import components from "./components.js";
import menu from "../utils/menu.js";
import {pageEnter, pageLeave} from "./loader.js";

/**
 * L’idea di questo file è avere un posto unico dove istanziare e integrare tra di
 * loro i vari vendor. Anche se solo poche di queste istanze possono tornare utili in
 * altre parti del codice, vengono comunque esportate tutte per comodità.
 */

/**
 * ScrollTrigger e Observer per GSAP.
 */
gsap.registerPlugin(ScrollTrigger, Observer);

/**
 * MouseFollower
 *
 * @link https://github.com/Cuberto/mouse-follower
 */
MouseFollower.registerGSAP(gsap);

let cursor;
if (window.matchMedia("(hover)").matches) {
    // No Touchscreen
    cursor = new MouseFollower({
        className: 'c-cursor',
        innerClassName: 'c-cursor_inner',
        textClassName: 'c-cursor_text',
        mediaClassName: 'c-cursor_media',
        mediaBoxClassName: 'c-cursor_media-box',
        iconSvgClassName: 'c-cursor_sprite',
        iconSvgSrc: m6e.spriteUrl,
        stickDelta: 0.2,
        skewing: 4
    });
}

/**
 * Istanza di Lenis, sincronizzata con ScrollTrigger.
 *
 * @link https://github.com/darkroomengineering/lenis/blob/v1.2.3/README.md#gsap-scrolltrigger
 */
const lenis = new Lenis();

lenis.on("scroll", ScrollTrigger.update);
gsap.ticker.add((time) => {
    lenis.raf(time * 1000);
});

gsap.ticker.lagSmoothing(0);

/**
 * https://github.com/darkroomengineering/lenis/blob/v1.2.3/README.md#methods
 */
function scrollToTop() {
    lenis.scrollTo(0, {immediate: true});
}

/**
 * https://barba.js.org/docs/advanced/third-party/#WordPress
 */
export function fixWordPressBodyClass(next) {
    const matches = next.html.match(/<body.+?class="([^"]*)"/i);
    document.body.setAttribute("class", (matches && matches.at(1)) ?? "");
}

/**
 * Esporto tutte le istanze per pura comodità.
 */
export * from "gsap";
export * from "gsap/ScrollTrigger";
export * from "gsap/Observer";
export {
    barba,
    cursor,
    lenis,
}

export default () => {
    /**
     * Barba.js
     */
    barba.init({
        timeout: 5000,
        transitions: [
            {
                name: "post",
                from: {namespace: ["post"]},
                to: {namespace: ["post"]},
                enter({current, next}) {
                    return gsap.timeline()
                        .set(current.container, {zIndex: 1})
                        .set(next.container, {zIndex: 2})
                        .to(next.container, {
                            y: -next.container.getBoundingClientRect().top
                        })
                        .set(next.container, {y: 0});
                },
            },
            {
                name: "default",
                sync: false,
                leave({current}) {
                    return pageLeave({
                        onComplete: () => {
                            current.container.style.display = "none";
                        }
                    });
                },
                beforeEnter({next}) {
                    fixWordPressBodyClass(next);
                },
                enter() {
                    // chiude il menu se è aperto
                    menu().close();

                    return pageEnter({
                        delay: 0.5,
                    });
                }
            },
            {
                name: "self",
                enter() {
                    menu().close();
                }
            },
        ],
        views: [
            {
                namespace: "post",
                afterEnter({next}) {
                    setTimeout(() => {
                        const nextPreview = next.container.querySelector(".js-navigation-next");

                        if (!nextPreview?.dataset.url) {
                            return;
                        }

                        // prefetch per evitare ritardi
                        barba.prefetch(nextPreview.dataset.url);

                        ScrollTrigger.create({
                            trigger: nextPreview,
                            end: "top top",
                            once: true,
                            onLeave: () => {
                                // Quando l’utente scrolla oltre, cambia pagina
                                barba.go(nextPreview.dataset.url);
                            }
                        });
                    });
                }
            },
        ]
    });

    barba.hooks.afterLeave(() => {
        components().beforeTransition();
        ScrollTrigger.killAll();
    });

    barba.hooks.after(() => {
        components().init();

        // GTM – Google Tag Manager
        const originalLocation = window.location.pathname + window.location.search;

        (function () {
            if (typeof window.dataLayer !== 'object') {
                console.warn('GTM is not loaded on the page');
                return;
            }

            const url = window.location.pathname + window.location.search;
            const title = document.title;

            window.dataLayer.push({
                event: 'VirtualPageview',
                originalLocation: originalLocation,
                virtualPageURL: url,
                virtualPageTitle: title
            });
        }());
    });

    barba.hooks.afterEnter(({next}) => {
        scrollToTop();

        // Forzo Iubenda
        let acceptedPurposes = window._iub?.cs?.consent?.purposes || {};
        next.container.querySelectorAll("iframe[suppressedsrc]").forEach(el => {
            if (
                !el.src
                || !el.src.includes("iubenda")
                || el.src === el.getAttribute("suppressedsrc")
            ) {
                // già risolto
                return;
            }

            let iframePurposes = el.getAttribute("data-iub-purposes");

            if (!iframePurposes) {
                return;
            }

            let iframePurposeId = parseInt(iframePurposes.split(',')[0], 10);

            if (acceptedPurposes[iframePurposeId]) {
                // rigenero il tag iframe per averlo più pulito e senza gli attributi strani di Iubenda
                const iframe = document.createElement('iframe');

                iframe.src = el.getAttribute("suppressedsrc");

                // Copio tutti gli attributi validi che possono essere presenti nel vecchio iframe
                // @link https://developer.mozilla.org/en-US/docs/Web/HTML/Reference/Elements/iframe
                ["width", "height", "frameborder", "allow", "allowfullscreen", "loading", "name", "referrerpolicy", "sandbox", "srcdoc"].forEach(attribute => {
                    if (el.hasAttribute(attribute)) {
                        iframe.setAttribute(attribute, el.getAttribute(attribute));
                    }
                });

                // Sostituisco il vecchio iframe con il nuovo
                el.replaceWith(iframe);
            }
        });

        // Forzo i video in autoplay a partire (a volte su Chrome non vanno in automatico)
        next.container.querySelectorAll("video[autoplay]").forEach(video => {
            video.play().catch(err => {
                console.warn('Autoplay failed:', err);
            });
        })
    });

    // GTM – Google Tag Manager
    const originalLocation = window.location.pathname + window.location.search;

    barba.hooks.after(() => {
        if (typeof window.dataLayer !== 'object') {
            console.warn('GTM is not loaded on the page');
            return;
        }

        const url = window.location.pathname + window.location.search;
        const title = document.title;

        window.dataLayer.push({
            event: 'VirtualPageview',
            originalLocation: originalLocation,
            virtualPageURL: url,
            virtualPageTitle: title
        });
    });
}
